import React from "react";
import { API } from "../API";
import { Loading as LoadingObj } from "../Loading";
import { CaregiverPatientDocument } from "../messages";
import { VisitInstanceId } from "../messages/ObjectIds";
import { AppState } from "../system/AppState";
import { AppCaregiver } from "../system/AppState/AppCaregiverState";
import useVisitInstanceDocuments from "./useVisitInstanceDocuments";
import useVisitInstancePlanOfCare, { InitialPlanOfCare } from "./useVisitInstancePlanOfCare";

interface Props {
  api: API;
  appState: AppState;
  caregiver: AppCaregiver | LoadingObj;
  visitInstanceId: VisitInstanceId;
  onClickDocument: (document: CaregiverPatientDocument) => void;
  onClickPlanOfCare: (planOfCare: InitialPlanOfCare) => void;
}

export declare namespace VisitInstanceDocumentsAndPlanOfCareState {
  export interface Loading {
    type: "Loading";
  }

  export interface Ready {
    type: "Ready";
    documents: CaregiverPatientDocument[];
    planOfCare: InitialPlanOfCare | null;
    isUploadingDocument: boolean;
  }
}

export type VisitInstanceDocumentsAndPlanOfCareState =
  | VisitInstanceDocumentsAndPlanOfCareState.Loading
  | VisitInstanceDocumentsAndPlanOfCareState.Ready;

export const useVisitInstanceDocumentsAndPlanOfCareActivity = (props: Props) => {
  const [state, setState] = React.useState<VisitInstanceDocumentsAndPlanOfCareState>({
    type: "Loading",
  });

  const {
    documentsState,
    isUploadingDocument,
    promptDeleteFreeScan,
    uploadNewDocument,
    _internals: _visitInstanceDocumentsInternals,
  } = useVisitInstanceDocuments({
    api: props.api,
    caregiver: props.caregiver,
    visitInstanceId: props.visitInstanceId,
  });

  const { planOfCareState, _internals: _visitInstancePlanOfCareInternals } =
    useVisitInstancePlanOfCare({
      api: props.api,
      appState: props.appState,
      caregiver: props.caregiver,
      visitInstanceId: props.visitInstanceId,
    });

  React.useEffect(() => {
    if (documentsState.type === "Loading" || planOfCareState.type === "Loading") {
      return setState({ type: "Loading" });
    }

    let planOfCare: InitialPlanOfCare | null = null;
    let documents: CaregiverPatientDocument[] = [];

    if (planOfCareState.type === "Ready") {
      planOfCare = planOfCareState.planOfCare;
    }

    if (documentsState.type === "Ready") {
      documents = [...documentsState.documents, ...documentsState.freeScans];
    }

    setState({
      type: "Ready",
      planOfCare: planOfCare,
      documents: documents,
      isUploadingDocument: isUploadingDocument,
    });
  }, [documentsState, documentsState.type, isUploadingDocument, planOfCareState]);

  return {
    state: state,
    actions: {
      promptDeleteFreeScan: promptDeleteFreeScan,
      uploadNewDocument: uploadNewDocument,
    },
    _internals: {
      ..._visitInstanceDocumentsInternals,
      ..._visitInstancePlanOfCareInternals,
    },
  };
};
